<template>
  <div
    v-if="preferences"
    class="preference-config-list m-3"
  >
    <h3 class="d-flex align-items-center py-3 mb-0 title-bar">
      <AdditionalExplanation
        class="ms-3 text-size"
        :text="trans('subscription.classification_config.classification_config_explanation')"
      >
        <span
          class="fa fa-info-circle"
        />
      </AdditionalExplanation>
      {{ trans('subscription.classification_config.add_classification_config') }}
    </h3>
    <GroupConfigList
      v-model="groups"
      :selector-context="selectorContext"
      :program-id="programId"
    />
    <div :key="refreshCount">
      <template
        v-for="(preference, preferenceIndex) in preferences"
      >
        <PreferenceConfig
          :key="preferenceIndex"
          v-model="preferences[preferenceIndex]"
          :preference-name="preferenceIndex"
          :preference-names="preferenceNames"
          :group-names="getGroupNamesBaseOnIndex(preference.index)"
          :is-loading="isLoading"
          @Update="update"
          @Remove="remove"
          @Copy="copy"
        />
      </template>
    </div>
    <div class="d-flex flex-row-reverse my-1 p-2">
      <button
        type="button"
        class="btn btn-primary mx-3"
        :disabled="isLoading"
        @click="add()"
      >
        {{ trans('subscription.classification_config.add_classification_config') }}
      </button>
    </div>
  </div>
</template>

<script>
import { selectorUpdateSelectorGroup } from '@/employees/components/Selector/utilities/builder/selectorBuilder'
import toast from '@/services/toast'
import GroupConfigList from '@/employees/components/GroupConfig/GroupConfigList'
import PreferenceConfig from '@/employees/components/PreferenceConfig/PreferenceConfig'
import AdditionalExplanation from '@/employees/components/AdditionalExplanation'
import _cloneDeep from 'lodash/cloneDeep'
export default {
  name: 'PreferenceConfigList',
  components: {
    GroupConfigList,
    PreferenceConfig,
    AdditionalExplanation,
  },
  provide () {
    return {
      programId: this.programId,
    }
  },
  props: {
    value: {
      required: false,
      type: [String, Array],
      default: null,
    },
    programId: {
      required: true,
      type: [Number, String],
    },
  },
  data () {
    return {
      preferences: null,
      groups: null,
      index: 0,
      refreshCount: 0,
      isLoading: false,
    }
  },
  computed: {
    preferenceNames () {
      if (this.preferences != null) {
        return Object.keys(this.preferences).map(name => name.toLowerCase())
      }
      return []
    },
    // Add mechanic for getting the missing term names
    selectorContext () {
      const selectorContext = { groupNames: [] }
      if (this.preferences) {
        selectorContext.groupNames = selectorContext.groupNames
          .concat(Object.values(this.preferences)
            .filter(preference => preference.groups)
            .map(preference => preference.groups.map(group => group))
            .flat()
            .map(group => {
              return { id: group.name, name: group.name, term: group?.term }
            }))
      }
      if (this.groups) {
        selectorContext.groupNames = selectorContext.groupNames
          .concat(this.groupPreferenceIdNames)
      }
      return selectorContext
    },
    groupPreferenceIdNames () {
      if (this.groups) {
        return Object.entries(this.groups).map(([key, value]) => {
          return {
            id: key,
            name: key,
            term: value?.term,
          }
        })
      }
      return []
    },
  },
  watch: {
    preferences: {
      deep: true,
      handler (newValue) {
        if (newValue) {
          this.$emit('input', JSON.stringify(this.removeDataToCreatValidConfigArray(newValue, this.groups)))
        }
      },
    },
    groups: {
      deep: true,
      handler (newValue) {
        if (newValue) {
          this.$emit('input', JSON.stringify(this.removeDataToCreatValidConfigArray(this.preferences, newValue)))
        }
        this.refreshCount++
      },
    },
  },
  created () {
    //* this is used for the preference config groups name validation
    this.$store.dispatch('getGroups', { queryParams: { filter: { programId: this.programId } } })
    //* this code is used to parse the preference configs and group configs
    if (this.value != null && !Array.isArray(this.value)) {
      this.isLoading = true
      const configArray = JSON.parse(this.value)

      if (configArray) {
        this.configArrayToObject(configArray)
      }

      if (this.preferences) {
        this.addDataToPreferences(this.preferences, Object.keys(this.preferences))
      } else {
        this.preferences = {}
      }

      this.isLoading = false
    } else {
      this.preferences = {}
    }

    this.$root.$on('ConfigGroupUpdate', this.configGroupUpdate)
  },
  destory () {
    this.$store.dispatch('clear_groups')
    this.$root.$off('ConfigGroupUpdate', this.configGroupUpdate)
  },
  methods: {
    // #region  Preferences CUD (create, update delete) + copy
    add () {
      if (this.preferences) {
        this.isLoading = true
        const preferenceReference = Lang.trans('subscription.classification_config.classification_config') + ' ' + crypto.randomUUID().substring(0, 8)
        this.index++
        this.$set(this.preferences, preferenceReference, { index: this.index, groups: [] })
        this.isLoading = false
      }
    },
    update ({ newPreferenceName, oldPreferenceName, baseSelector }) {
      if (this.preferences) {
        this.isLoading = true
        const newPreferences = {}
        for (const [key, value] of Object.entries(this.preferences)) {
          if (key === oldPreferenceName) {
            newPreferences[newPreferenceName] = _cloneDeep(value)
            if (baseSelector) {
              newPreferences[newPreferenceName].base_selector = baseSelector.selector
            }
          } else {
            newPreferences[key] = value
          }
        }
        this.preferences = newPreferences
        this.isLoading = false
      }
    },
    remove (preferenceName) {
      if (this.preferences) {
        this.isLoading = true
        this.$delete(this.preferences, preferenceName)
        this.isLoading = false
      }
    },
    copy ({ preferenceName, groups }) {
      if (this.preferences) {
        this.isLoading = true
        this.index++
        for (let i = 0, length = groups.length; i < length; i++) {
          groups[i].name = groups[i].name + ' ' + crypto.randomUUID().substring(0, 8)
          this.$store.dispatch('set_group', {
            name: groups[i].name,
            max: groups[i].max,
            term: groups[i].term,
          })
        }
        this.$set(this.preferences, preferenceName, {
          index: this.index,
          groups,
        })
        this.isLoading = false
        toast.info(Lang.trans('subscription.classification_config.classification_config_copy'))
      }
    },
    // #endregion
    // #region Parse saved config array from database to an object keyed by the name for preferences and groups
    configArrayToObject (configArray) {
      this.preferences = {}
      this.groups = {}
      configArray.forEach(config => {
        if (config.type === 'preference') {
          this.$set(this.preferences, config.name, config)
        } else if (config.type === 'group') {
          this.$set(this.groups, config.name, config)
        }
      })
    },
    // #endregion
    // #region Parse preference config JSON to useable perference object
    addDataToPreferences (preferences, preferencesKeys) {
      for (let i = 0, length = preferencesKeys.length; i < length; i++) {
        preferences[preferencesKeys[i]].index = this.index
        this.addPreferenceGroupToStore(preferences[preferencesKeys[i]].groups)
        this.index++
      }
    },
    addPreferenceGroupToStore (groups) {
      for (let j = 0, groupsLength = groups.length; j < groupsLength; j++) {
        this.$store.dispatch('set_group', {
          name: groups[j].name,
          max: groups[j].max,
          term: groups[j].term,
        })
      }
    },
    // #endregion
    // #region Get group name based on index
    getGroupNamesBaseOnIndex (index) {
      let groupNames = []
      if (this.preferences) {
        groupNames = groupNames
          .concat(Object.values(this.preferences)
            .filter(preference => preference.groups && preference.index < index)
            .map(preference => preference.groups.map(group => group))
            .flat()
            .map(group => { return { id: group.name, name: group.name, term: group?.term } }))
      }
      if (this.groups) {
        groupNames = groupNames
          .concat(this.groupPreferenceIdNames)
      }
      return groupNames
    },
    // #endregion
    // #region Update selector using old config name
    configGroupUpdate ({ oldName, newName, preferenceIndex }) {
      if (oldName !== newName) {
        this.isLoading = true
        for (const preference of Object.values(this.preferences)) {
          this.updateConfigs(preference, oldName, newName, preferenceIndex)
        }
        this.refreshCount++
        this.isLoading = false
      }
    },
    updateConfigs (preference, oldName, newName, preferenceIndex) {
      if (preferenceIndex) {
        if (!preference.index < preferenceIndex) {
          if (preference.base_selector) {
            selectorUpdateSelectorGroup(preference.base_selector, oldName, newName)
          }
          this.updateGroups(preference, oldName, newName)
        }
      } else {
        if (preference.base_selector) {
          selectorUpdateSelectorGroup(preference.base_selector, oldName, newName)
        }
        this.updateGroups(preference, oldName, newName)
      }
    },
    updateGroups (preference, oldName, newName) {
      if (preference.groups) {
        for (let i = 0, groupsLength = preference.groups.length; i < groupsLength; i++) {
          if (preference.groups[i].selectors) {
            this.updateGroupSelectors(preference.groups[i].selectors, oldName, newName)
          }
        }
      }
    },
    updateGroupSelectors (selectors, oldName, newName) {
      for (let j = 0, selectorsLength = selectors.length; j < selectorsLength; j++) {
        if (selectors[j].selector) {
          selectorUpdateSelectorGroup(selectors[j].selector, oldName, newName)
        }
      }
    },
    // #endregion
    // #region Parse perference object to valid preference config JSON
    removeDataToCreatValidConfigArray (preferences, groups) {
      const clonePreferences = _cloneDeep(preferences)
      const cloneGroups = _cloneDeep(groups)
      const configArray = []

      for (const [key, value] of Object.entries(cloneGroups)) {
        value.name = key
        value.type = 'group'
        configArray.push(value)
      }

      for (const [key, value] of Object.entries(clonePreferences)) {
        delete value.index
        delete value.preferenceName
        this.removeEmptySelectorsFromGroups(clonePreferences[key].groups)
        value.type = 'preference'
        configArray.push(value)
      }

      return configArray
    },
    removeEmptySelectorsFromGroups (groups) {
      for (let i = 0, groupslength = groups.length; i < groupslength; i++) {
        this.removeEmptySelectorsFromGroup(groups[i].selectors)
      }
    },
    removeEmptySelectorsFromGroup (selectors) {
      const deleteSelectorIndex = []
      //* find the empty selectors
      for (let j = 0, selectorsLength = selectors.length; j < selectorsLength; j++) {
        if (selectors[j].selector === null) {
          deleteSelectorIndex.push(j)
        }
      }
      //* remove the empty selectors
      //* traverse the array backwards to not effect the the indexs of the selectors array
      for (let j = deleteSelectorIndex.length - 1; j >= 0; j--) {
        selectors.splice(deleteSelectorIndex[j], 1)
      }
    },
    // #endregion
  },
}
</script>

<style>
.visibility-collapse {
  visibility: collapse;
}
.preference-config-list {
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 3px 10px 0 rgba(0, 0, 0, 0.1);
}
.title-bar {
  background-color: rgba(0, 0, 0, 0.06);
  border-bottom: solid;
  border-bottom-width: 1px;
  border-bottom-color: rgba(0, 0, 0, 0.2);
  width: 100%;
}
.btn-primary {
  background-color: #25167a !important;
}
</style>
